import React from "react";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Grid from "components/common/Grid";
import AnchorElTooltips from "components/common/TooltipHover";
import { usePhcTheme } from "../PhcThemeContext";
import { getMetricInsight } from "../metricInsights";

const RING_SIZE_DESKTOP = 82;
const RING_SIZE_MOBILE = 64;
const STROKE = 12;

const MetricRing = ({ progress = 0, color = "#17E9D9", size = RING_SIZE_DESKTOP }) => {
  const { theme } = usePhcTheme();
  const radius = (size - STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      <Box
        component="svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        sx={{ display: "block", transform: "rotate(-90deg)" }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={theme.chartGridColor}
          strokeWidth={STROKE}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.6s ease, stroke 0.3s ease" }}
        />
      </Box>
    </Box>
  );
};

const MetricCard = ({ metric }) => {
  const { theme } = usePhcTheme();
  const insight = getMetricInsight(metric);
  const apiTooltip = insight.info || "";
  const descriptionText = (metric?.tooltip || insight.description || "")
    .replace(new RegExp(`^${insight.statusLabel}\\s*[—-]\\s*`, "i"), "")
    .trim();

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: { xs: 231, md: 237 },
        borderRadius: { xs: "16px", md: theme.radiusCard },
        border: {
          xs: theme.mobileMetaBorder,
          md: `1px solid ${theme.sectionBorderSoft || theme.sectionBorder}`,
        },
        backgroundImage: {
          xs: "none",
          md: theme.sectionBg,
        },
        backgroundColor: {
          xs: theme.mobileMetaBg,
          md: theme.cardBg,
        },
        backdropFilter: { xs: "blur(5px)", md: "none" },
        boxShadow: theme.cardShadow,
        overflow: "hidden",
        px: { xs: 2, md: 1.5 },
        pt: { xs: 3, md: "23px" },
        pb: { xs: 2.5, md: 2 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: theme.insetGlow,
        },
      }}
    >
      {!!apiTooltip && (
        <Box
          sx={{
            position: "absolute",
            top: { xs: 14, md: 23 },
            right: { xs: 14, md: 16 },
            zIndex: 3,
            lineHeight: 0,
          }}
        >
          <AnchorElTooltips title={apiTooltip} placement="top">
            <Box
              component="img"
              src="/images/phc-dashboard/metric-info.svg"
              alt="info"
              sx={{
                width: { xs: 16, md: 24 },
                height: { xs: 16, md: 24 },
                display: "block",
                cursor: "pointer",
                opacity: 0.9,
              }}
            />
          </AnchorElTooltips>
        </Box>
      )}

      <Stack
        spacing={{ xs: 1.5, md: "13px" }}
        alignItems="center"
        sx={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 207 }}
      >
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          <MetricRing
            progress={insight.progress}
            color={insight.statusColor}
            size={RING_SIZE_MOBILE}
          />
        </Box>
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <MetricRing
            progress={insight.progress}
            color={insight.statusColor}
            size={RING_SIZE_DESKTOP}
          />
        </Box>

        <Stack spacing="4px" alignItems="center" sx={{ width: "100%" }}>
          <Text
            sx={{
              fontWeight: 700,
              fontSize: { xs: 30, md: 24 },
              color: theme.textPrimary,
              lineHeight: { xs: "30px", md: "normal" },
              whiteSpace: "nowrap",
            }}
          >
            {metric.value ?? "-"}
          </Text>
          <Text
            sx={{
              fontWeight: 500,
              fontSize: { xs: 14, md: 16 },
              color: { xs: theme.textMuted, md: theme.textPrimary },
              textAlign: "center",
              lineHeight: "normal",
              whiteSpace: "nowrap",
            }}
          >
            {metric.label}
          </Text>
          {(insight.statusLabel || descriptionText) && (
            <Text
              sx={{
                fontWeight: 400,
                fontSize: 12,
                color: theme.textMuted,
                textAlign: "center",
                lineHeight: 1.35,
                // maxWidth: { xs: 280, md: 168 },
                px: 0.5,
              }}
            >
              {insight.statusLabel && (
                <Box
                  component="span"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: 12, md: 14 },
                    color: insight.statusColor,
                  }}
                >
                  {insight.statusLabel}
                </Box>
              )}
              {insight.statusLabel && descriptionText ? " — " : ""}
              {descriptionText}
            </Text>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

const PortfolioMetrics = ({ metrics = [] }) => {
  const { theme } = usePhcTheme();
  if (!metrics?.length) return null;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: { xs: 0, md: theme.radiusSection },
        border: {
          xs: "none",
          md: `1px solid ${theme.sectionBorderSoft || theme.sectionBorder}`,
        },
        backgroundImage: { xs: "none", md: theme.sectionBg },
        backgroundColor: { xs: "transparent", md: theme.cardBg },
        boxShadow: { xs: "none", md: theme.cardShadow },
        p: { xs: 0, md: 4 },
        overflow: "hidden",
        "&::after": {
          content: { xs: "none", md: '""' },
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: theme.sectionInsetGlow,
        },
      }}
    >
      <Text
        sx={{
          position: "relative",
          zIndex: 1,
          display: { xs: "none", md: "block" },
          fontWeight: 600,
          fontSize: 24,
          color: theme.textPrimary,
          mb: 3,
        }}
      >
        Portfolio Metrics
      </Text>

      <Grid
        container
        spacing={{ xs: 2, md: 2 }}
        alignItems="stretch"
        sx={{ position: "relative", zIndex: 1 }}
      >
        {metrics.map((metric) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={2.4}
            key={metric.key || metric.label}
            sx={{ display: "flex" }}
          >
            <MetricCard metric={metric} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PortfolioMetrics;
